/**
 * FRE-1274 — Neighborhood color resolution.
 *
 * Strategy:
 * 1. **Curated overrides** — `neighborhoodColorsLight` / `neighborhoodColorsDark`
 *    remain the preferred palette for the locked CLT set (AffinityRing,
 *    marketing grids). Sibling PIP-178 expands that curated set to 23 while
 *    keeping hand-picked hues for visual separability.
 * 2. **Deterministic hash → palette slot** — any key not in the curated map
 *    resolves via `hashString` into the curated values used as rotating
 *    palette slots. Adding a neighborhood never requires a type-checked token
 *    edit for UI to render; curated entries always win over the hash path.
 *
 * Keys are normalized by lowercasing and stripping hyphens so kebab URL slugs
 * (`south-end`) and camelCase token keys (`southend`) share one lookup.
 */
import { hashString } from '../utils/hash';
import { neighborhoodColorsDark, neighborhoodColorsLight } from './design-tokens';
/** Absolute fallback when a curated map is empty (should never happen in prod). */
const ABSOLUTE_FALLBACK = {
    primary: '#0D9488',
    light: '#CCFBF1',
    dark: '#134E4A',
};
/**
 * Normalize a neighborhood identifier for color lookup.
 * `south-end` / `South End` / `southend` → `southend`.
 */
export function normalizeNeighborhoodColorKey(key) {
    return key
        .trim()
        .toLowerCase()
        .replace(/[\s_-]+/g, '');
}
/**
 * Resolve a neighborhood color scale from a curated map, falling back to a
 * deterministic palette slot when the key is unknown.
 */
export function resolveNeighborhoodColorScale(key, curated) {
    const normalized = normalizeNeighborhoodColorKey(key);
    if (!normalized)
        return ABSOLUTE_FALLBACK;
    const hit = curated[normalized];
    if (hit)
        return hit;
    const palette = Object.values(curated);
    if (palette.length === 0)
        return ABSOLUTE_FALLBACK;
    return palette[hashString(normalized) % palette.length] ?? ABSOLUTE_FALLBACK;
}
/**
 * Resolve a neighborhood color for the active theme mode using the package
 * curated maps + hash fallback.
 */
export function getNeighborhoodColorScale(key, mode) {
    const curated = mode === 'dark' ? neighborhoodColorsDark : neighborhoodColorsLight;
    return resolveNeighborhoodColorScale(key, curated);
}
